import {
  ReactElement,
  ReactNode,
  useCallback,
  useMemo,
  useRef,
  useState,
} from "react";
import { withRouter } from "react-router-dom";

import {
  ActionToolbar,
  DialogWithActions,
  EditButton,
  GenerateUsersLatestPrivacyReport,
  GqlTable,
  ResetUserActivationButtonWithDialog,
} from "../../components";

import { Button, Container, Tooltip } from "@material-ui/core";
import TableCell from "@material-ui/core/TableCell";

import { useUserState } from "../../../context";
import { QUERIES } from "../../../graphql";
import {
  CreateSystemUserData,
  UpdateSystemUserData,
  User,
  UserBuilding,
} from "../../../graphql/models";
import { UserForm } from "./user_form";

import DoneAllIcon from "@material-ui/icons/DoneAll";
import WarningIcon from "@material-ui/icons/Warning";
import ErrorOutlineIcon from "@material-ui/icons/ErrorOutline";
import Chip from "@material-ui/core/Chip";
import { green, orange, red } from "@material-ui/core/colors";
import useUsersStyle from "./users.style";
import { UserDevicesWithButtonDialog } from "../../components/user_devices";
import { UserPrivacyStatus } from "../../components/user_privacy_status";
import { useDateTimeUtils } from "../../../hooks";
import { AccountCircle } from "@material-ui/icons";
import { DateTime } from "luxon";

function UserStatusChip(props) {
  const { user }: { user: User } = props;

  const chipIcon = useMemo(() => {
    if (user.status === "active") {
      return <DoneAllIcon />;
    } else if (user.status === "pending") {
      return <WarningIcon />;
    } else if (user.status === "blocked") {
      return <ErrorOutlineIcon />;
    }
    return <DoneAllIcon />;
  }, [user.status]);

  const chipColor = useMemo(() => {
    if (user.status === "active") {
      return green[500];
    } else if (user.status === "pending") {
      return orange[500];
    } else if (user.status === "blocked") {
      return red[500];
    }
    return red[500];
  }, [user.status]);

  const chipLabel = useMemo(() => {
    if (user.status === "active") {
      return "attivo";
    } else if (user.status === "pending") {
      return "non verificato";
    } else if (user.status === "blocked") {
      return "bloccato dal sistema";
    }
  }, [user.status]);

  return (
    <Chip
      variant="outlined"
      style={{
        backgroundColor: chipColor,
      }}
      label={chipLabel}
    />
  );
}

function UsersPage(props: any) {
  const classes = useUsersStyle();
  const { userRole, userBuilding } = useUserState();

  const tableRef = useRef();

  const [formDialogOpened, setFormDialogOpened] = useState(false);
  const [formUser, setFormUser] = useState<
    CreateSystemUserData | UpdateSystemUserData | undefined
  >(void 0);

  const { displayDateTime } = useDateTimeUtils();

  const addNewUser = () => {
    setFormUser({
      role: "user",
    } as CreateSystemUserData | UpdateSystemUserData);
    setFormDialogOpened(true);
  };

  const editUser = (user) => {
    setFormUser({
      ...user,
    } as CreateSystemUserData | UpdateSystemUserData);
    setFormDialogOpened(true);
  };

  const onValueUpdatedSuccessfully = () => {
    setFormUser(void 0);
    setFormDialogOpened(false);
    (tableRef as any).current?.refresh();
  };

  const gqlTableToolbarActions = useMemo(() => {
    const actions: ReactNode[] = [];
    if (userRole === "system_admin") {
      actions.push(
        <Button
          key="addNewUser"
          variant="contained"
          color="primary"
          onClick={() => addNewUser()}
        >
          Aggiungi
        </Button>
      );
      actions.push(<GenerateUsersLatestPrivacyReport />);
    }

    return actions;
  }, [userRole]);

  const gqlTableRowActions = useCallback(
    (row: User) => {
      const actions: ReactElement[] = [];
      if (userRole === "system_admin") {
        actions.push(<EditButton onClick={() => editUser(row)} />);
        if (row.role === "user") {
          actions.push(
            <UserDevicesWithButtonDialog userId={row.id}>
              Dispositivi
            </UserDevicesWithButtonDialog>
          );
        }
      }
      if (row.status !== "active") {
        actions.push(
          <ResetUserActivationButtonWithDialog
            key="ResetUserActivationButtonWithDialog"
            user={row}
          />
        );
      } else {
        if (row.role === "user") {
          actions.push(
            <UserPrivacyStatus key="UserPrivacyStatus" user={row} />
          );
        }
      }

      return actions;
    },
    [userRole]
  );

  const _toIntegerNumber = (value: number) => {
    console.debug(`value is `, value);
    const v = Math.ceil(Math.abs(value));
    console.debug(v);
    return v;
  };

  const userActiveStatus = (user: User) => {
    let color: string = "red";
    let date: string = "n.d.";
    let luxon: DateTime;
    if (!!user.lastTokenCheckedDate) {
      console.debug(user.lastTokenCheckedDate);
      date = displayDateTime(user.lastTokenCheckedDate);
      luxon = DateTime.fromJSDate(new Date(user.lastTokenCheckedDate));
      if (_toIntegerNumber(luxon.diffNow("hours").hours) <= 24)
        color = "LightGreen";
      else if (_toIntegerNumber(luxon.diffNow("days").days) <= 5)
        color = "gold";
      else if (_toIntegerNumber(luxon.diffNow("weeks").weeks) <= 3)
        color = "orange";
    }
    return (
      <>
        <Tooltip title={`Ultimo check ${date}`}>
          <AccountCircle
            style={{ color: color }}
            fontSize="small"
          ></AccountCircle>
        </Tooltip>
        &nbsp;{user.username}
      </>
    );
  };

  const filterBuildingsCollection = useCallback(
    (collection: UserBuilding[]) => {
      if (userBuilding) {
        return collection.filter((v) => v.id === userBuilding.id);
      }

      return collection;
    },
    [userBuilding]
  );

  return (
    <Container className={classes.container} maxWidth={false} component="div">
      <ActionToolbar title={"Utenti"} actions={gqlTableToolbarActions} />
      <GqlTable
        ref={tableRef}
        columns={[
          "",
          "Stato",
          "Username",
          "Nome",
          "Email/Telefono",
          "Ruolo",
          "Palazzi / Appartamenti",
          "Richiesto cambio password?",
        ].map((v, index) => (
          <TableCell key={index}>{v}</TableCell>
        ))}
        query={QUERIES.USERS}
        queryResultDataField="users"
        rows={(row: User) => (
          <>
            <TableCell style={{ whiteSpace: "nowrap" }}>
              {gqlTableRowActions(row)}
            </TableCell>
            <TableCell style={{ textAlign: "center" }}>
              <UserStatusChip user={row} />
            </TableCell>
            <TableCell component="th" scope="row">
              {userActiveStatus(row)}
              {row.isTestAccount ? (
                <div style={{ opacity: 0.5 }}>Test account</div>
              ) : null}
            </TableCell>
            <TableCell>
              {row.firstName}
              <br />
              {row.lastName}
            </TableCell>
            <TableCell>
              {row.email}
              <br />
              {row.phone}
            </TableCell>
            <TableCell>{row.role}</TableCell>
            <TableCell>
              {row.buildings && (
                <ul>
                  {filterBuildingsCollection(row.buildings).map(
                    (building, index) => (
                      <li key={index}>
                        {building.name}
                        {building.apartments && (
                          <ul>
                            {building.apartments.map((apartment, index) => (
                              <li key={index}>
                                {apartment.code} - {apartment.role}
                              </li>
                            ))}
                          </ul>
                        )}
                      </li>
                    )
                  )}
                </ul>
              )}
            </TableCell>
            <TableCell>{row?.passwordChangeRequired ? "Sì" : ""}</TableCell>
          </>
        )}
      />
      <DialogWithActions
        fullScreen
        open={formDialogOpened}
        setOpen={setFormDialogOpened}
        title={"Aggiungi / Modifica Utente"}
      >
        <UserForm
          value={formUser}
          onValueUpdatedSuccessfully={onValueUpdatedSuccessfully}
        />
      </DialogWithActions>
    </Container>
  );
}

export default withRouter(UsersPage);
